import React from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Container,
  useTheme,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Grid,
  Link
} from '@mui/material';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import { 
  Menu as MenuIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const PublicLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const isActivePath = (path: string) => location.pathname === path;

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="sticky" color="default" elevation={0}>
        <Toolbar>
          <Typography
            variant="h5"
            color="primary"
            noWrap
            component={RouterLink}
            to="/"
            sx={{ 
              flexGrow: 1, 
              textDecoration: 'none',
              fontWeight: 700,
              letterSpacing: '-0.5px',
              fontSize: '1.5rem'
            }}
          >
            Facto
          </Typography>

          {isMobile ? (
            <>
              <IconButton
                size="large"
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleMenu}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={open}
                onClose={handleClose}
              >
                <MenuItem 
                  component={RouterLink} 
                  to="/" 
                  onClick={handleClose}
                  selected={isActivePath('/')}
                  sx={{ fontSize: '16px' }}
                >
                  Inicio
                </MenuItem>
                <MenuItem 
                  component={RouterLink} 
                  to="/about" 
                  onClick={handleClose}
                  selected={isActivePath('/about')}
                  sx={{ fontSize: '16px' }}
                >
                  Acerca de
                </MenuItem>
                <MenuItem 
                  component={RouterLink} 
                  to="/contact" 
                  onClick={handleClose}
                  selected={isActivePath('/contact')}
                  sx={{ fontSize: '16px' }}
                >
                  Contacto
                </MenuItem>
                <Divider />
                {isAuthenticated ? (
                  <MenuItem 
                    component={RouterLink} 
                    to="/dashboard" 
                    onClick={handleClose}
                  >
                    Panel
                  </MenuItem>
                ) : (
                  <>
                    <MenuItem 
                      component={RouterLink} 
                      to="/login" 
                      onClick={handleClose}
                    >
                      Iniciar Sesión
                    </MenuItem>
                    <MenuItem 
                      component={RouterLink} 
                      to="/register" 
                      onClick={handleClose}
                    >
                      Registrarse
                    </MenuItem>
                  </>
                )}
              </Menu>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button 
                color="inherit" 
                component={RouterLink} 
                to="/"
                sx={{ 
                  mx: 1,
                  fontSize: '16px',
                  fontWeight: isActivePath('/') ? 600 : 400,
                  borderBottom: isActivePath('/') ? '2px solid' : 'none',
                  borderRadius: 0,
                  borderColor: 'primary.main'
                }}
              >
                Inicio
              </Button>
              <Button 
                color="inherit" 
                component={RouterLink} 
                to="/about"
                sx={{ 
                  mx: 1,
                  fontSize: '16px',
                  fontWeight: isActivePath('/about') ? 600 : 400,
                  borderBottom: isActivePath('/about') ? '2px solid' : 'none',
                  borderRadius: 0,
                  borderColor: 'primary.main'
                }}
              >
                Acerca de
              </Button>
              <Button 
                color="inherit" 
                component={RouterLink} 
                to="/contact"
                sx={{ 
                  mx: 1,
                  fontSize: '16px',
                  fontWeight: isActivePath('/contact') ? 600 : 400,
                  borderBottom: isActivePath('/contact') ? '2px solid' : 'none',
                  borderRadius: 0,
                  borderColor: 'primary.main'
                }}
              >
                Contacto
              </Button>
              {isAuthenticated ? (
                <Button 
                  color="primary" 
                  variant="contained" 
                  component={RouterLink} 
                  to="/dashboard"
                  sx={{ ml: 2 }}
                >
                  Panel
                </Button>
              ) : (
                <>
                  <Button 
                    color="inherit" 
                    component={RouterLink} 
                    to="/login"
                    sx={{ ml: 2 }}
                  >
                    Iniciar Sesión
                  </Button>
                  <Button 
                    color="primary" 
                    variant="contained" 
                    component={RouterLink} 
                    to="/register"
                    sx={{ ml: 1 }}
                  >
                    Registrarse
                  </Button>
                </>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1 }}>
        <Outlet />
      </Box>

      {/* Comprehensive Footer - REQ-FTR-01, REQ-FTR-02 */}
      <Box 
        component="footer" 
        sx={{ 
          py: 6, 
          px: 2, 
          mt: 'auto', 
          backgroundColor: 'grey.100',
          borderTop: '1px solid',
          borderColor: 'grey.300'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Company Info */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
                Facto
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Plataforma de factoraje que conecta empresas con inversionistas.
              </Typography>
              {/* Social Media Icons */}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <IconButton 
                  component="a" 
                  href="https://facebook.com/factoparaguay" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  size="small"
                  sx={{ color: 'text.secondary' }}
                >
                  <FacebookIcon />
                </IconButton>
                <IconButton 
                  component="a" 
                  href="https://twitter.com/facto_py" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Twitter"
                  size="small"
                  sx={{ color: 'text.secondary' }}
                >
                  <TwitterIcon />
                </IconButton>
                <IconButton 
                  component="a" 
                  href="https://linkedin.com/company/factopy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  size="small"
                  sx={{ color: 'text.secondary' }}
                >
                  <LinkedInIcon />
                </IconButton>
                <IconButton 
                  component="a" 
                  href="https://instagram.com/facto_py" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  size="small"
                  sx={{ color: 'text.secondary' }}
                >
                  <InstagramIcon />
                </IconButton>
              </Box>
            </Grid>

            {/* Company Links */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h6" color="text.primary" gutterBottom sx={{ fontWeight: 600, fontSize: '1rem' }}>
                Empresa
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link 
                  component={RouterLink} 
                  to="/about" 
                  color="text.secondary" 
                  underline="hover"
                  sx={{ fontSize: '14px' }}
                >
                  Acerca de
                </Link>
                <Link 
                  component={RouterLink} 
                  to="/contact" 
                  color="text.secondary" 
                  underline="hover"
                  sx={{ fontSize: '14px' }}
                >
                  Contacto
                </Link>
                <Link 
                  component={RouterLink} 
                  to="/faq" 
                  color="text.secondary" 
                  underline="hover"
                  sx={{ fontSize: '14px' }}
                >
                  Preguntas Frecuentes
                </Link>
              </Box>
            </Grid>

            {/* Legal Links */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h6" color="text.primary" gutterBottom sx={{ fontWeight: 600, fontSize: '1rem' }}>
                Legal
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link 
                  component={RouterLink} 
                  to="/privacy" 
                  color="text.secondary" 
                  underline="hover"
                  sx={{ fontSize: '14px' }}
                >
                  Política de Privacidad
                </Link>
                <Link 
                  component={RouterLink} 
                  to="/terms" 
                  color="text.secondary" 
                  underline="hover"
                  sx={{ fontSize: '14px' }}
                >
                  Términos y Condiciones
                </Link>
              </Box>
            </Grid>

            {/* Contact Info */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h6" color="text.primary" gutterBottom sx={{ fontWeight: 600, fontSize: '1rem' }}>
                Contacto
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
                  Email: info@facto.com.py
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
                  WhatsApp: +595 985 298 994
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
                  Asunción, Paraguay
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Copyright */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="body2" color="text.secondary" align="center" sx={{ fontSize: '14px' }}>
            © {new Date().getFullYear()} Facto. Todos los derechos reservados.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicLayout;
